import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SidebarNav from "../components/GettingStarted/SidebarNav";
import MainContent from "../components/GettingStarted/MainContent";

export default function GettingStarted() {
  const location = useLocation();
  const navigate = useNavigate();

  const mainTabs = [
    { id: "generalEV", label: "General EV Tips" },
    { id: "cars", label: "Cars" },
  ];

  const carTabs = [
    { id: "bolt", label: "2019 Chevy Bolt EV" },
    { id: "tesla", label: "2019 Tesla Model 3" },
    { id: "sienna", label: "2022 Toyota Sienna" },
    { id: "modelY", label: "2021 Tesla Model Y" },
  ];

  const carSubTabConfig = {
    bolt: [
      { id: "charging", label: "Charging" },
      { id: "driving", label: "Driving" },
      { id: "features", label: "Features" },
      { id: "faq", label: "FAQ" },
    ],
    tesla: [
      { id: "charging", label: "Charging" },
      { id: "driving", label: "Driving" },
      { id: "features", label: "Features" },
      { id: "faq", label: "FAQ" },
    ],
    sienna: [
      { id: "driving", label: "Driving" },
      { id: "features", label: "Features" },
      { id: "faq", label: "FAQ" },
    ],
    modelY: [
      { id: "charging", label: "Charging" },
      { id: "driving", label: "Driving" },
      { id: "features", label: "Features" },
      { id: "faq", label: "FAQ" },
    ],
  };

  // state
  const [activeMainTab, setActiveMainTab] = useState("general");
  const [activeCar, setActiveCar] = useState(null);
  const [activeCarSubTab, setActiveCarSubTab] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // restored for matchesSearch

  // parse hash on load / change
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (!hash) return;

    const parts = hash.split("-");
    if (parts[0] === "general") {
      setActiveMainTab("general");
      setActiveCar(null);
      setActiveCarSubTab(null);
    } else if (parts[0] === "cars") {
      setActiveMainTab("cars");
      setActiveCar(parts[1] || null);
      setActiveCarSubTab(parts[2] || null);
    }
  }, [location.hash]);

  // handlers
  const handleMainTabClick = (tabId) => {
    setActiveMainTab(tabId);
    setActiveCar(null);
    setActiveCarSubTab(null);
    navigate(`#${tabId}`, { replace: true });
  };

  const handleCarClick = (carId) => {
    setActiveMainTab("cars");
    setActiveCar(carId);
    setActiveCarSubTab(null);
    navigate(`#cars-${carId}`, { replace: true });
  };

  const handleCarSubTabClick = (carId, subId) => {
    setActiveMainTab("cars");
    setActiveCar(carId);
    setActiveCarSubTab(subId);
    navigate(`#cars-${carId}-${subId}`, { replace: true });
  };

  const matchesSearch = (text) => {
    let content = "";
    if (typeof text === "string") content = text;
    else if (text.text) content = text.text;
    else if (text.parts) content = text.parts.map((p) => p.text).join(" ");
    return content.toLowerCase().includes(searchQuery.toLowerCase());
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <SidebarNav
        mainTabs={mainTabs}
        carTabs={carTabs}
        carSubTabConfig={carSubTabConfig}
        activeMainTab={activeMainTab}
        activeCar={activeCar}
        activeCarSubTab={activeCarSubTab}
        handleMainTabClick={handleMainTabClick}
        handleCarClick={handleCarClick}
        handleCarSubTabClick={handleCarSubTabClick}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Mobile TOC + Subtabs Bar */}
      {(
        <div className="md:hidden sticky top-16 z-40 bg-white border-b shadow-sm">
          <div className="flex items-center gap-2 px-2 py-2 overflow-x-auto">
            {/* Contents toggle (opens the sidebar drawer) */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex items-center gap-2 bg-white border border-gray-300 shadow-sm px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100 whitespace-nowrap"
              aria-label="Open table of contents"
            >
              ☰
              <span className="text-sm font-medium">Table Of Contents</span>
            </button>

            {/* Inline sub‑tabs for the active car */}
            <div className="flex items-center gap-2">
              {carSubTabConfig[activeCar]?.map((sub) => {
                const isActive =
                  activeCarSubTab === sub.id ||
                  (!activeCarSubTab && sub.id === carSubTabConfig[activeCar][0].id);
                return (
                  <button
                    key={sub.id}
                    onClick={() => handleCarSubTabClick(activeCar, sub.id)}
                    className={`px-3 py-2 rounded-md text-sm whitespace-nowrap ${isActive
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                  >
                    {sub.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <main className="flex-grow py-12 md:pl-[320px]">
        <MainContent
          activeMainTab={activeMainTab}
          activeCar={activeCar}
          activeCarSubTab={activeCarSubTab}
          handleCarSubTabClick={handleCarSubTabClick}
          matchesSearch={matchesSearch}
          carTabs={carTabs}
        />
        {/* Show main tab options when no car is selected */}
        {activeMainTab == "general" && (
          <div className="mt-8 px-4">
            <h2 className="text-lg font-semibold mb-4">Getting Started</h2>
            <div className="flex flex-wrap gap-4">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleMainTabClick(tab.id)}
                  className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 shadow-sm"
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Show car options if Cars tab is active */}
        {activeMainTab === "cars" && !activeCar && (
          <div className="mt-8 px-4">
            <h2 className="text-lg font-semibold mb-4">Select a Car</h2>
            <div className="flex flex-wrap gap-4">
              {carTabs.map((car) => (
                <button
                  key={car.id}
                  onClick={() => handleCarClick(car.id)}
                  className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 shadow-sm"
                >
                  {car.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}